import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, CheckCircle2, XCircle, RefreshCw, Mail, MessageCircle, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOW_LABELS = {
  garantie: "Garanții",
  retur: "Retururi",
  schimb: "Schimb",
};

const FLOW_COLORS = {
  garantie: "emerald",
  retur: "sky",
  schimb: "violet",
};

/**
 * Diagnostic overlay: fetches `/api/returns/warranty/notification-audit`
 * and shows, for every flow × step, the mapped Email + WhatsApp templates
 * plus a red badge with the exact reason when a notification would NOT fire
 * (missing template, template not APPROVED by Meta, config disabled, ...).
 */
export default function NotificationAuditModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/returns/warranty/notification-audit`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Eroare încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const flows = data ? Object.keys(FLOW_LABELS).filter((f) =>
    (data.steps || []).some((s) => s.flow === f),
  ) : [];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="notif-audit-modal">
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/15 text-amber-300">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-zinc-100">Audit Notificări Retur/Garanție</h2>
              <p className="text-[11px] text-zinc-500">
                Verifică ce template Email + WhatsApp e mapat la fiecare pas — și de ce nu se trimite unde lipsește.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="audit-refresh-btn"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 text-xs text-zinc-200 hover:border-zinc-600 disabled:opacity-40"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              Reîmprospătează
            </button>
            <button data-testid="audit-close-btn" onClick={onClose} className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Global banners */}
        {data && (
          <div className="flex flex-wrap gap-2 border-b border-zinc-800 bg-zinc-900/40 px-5 py-2.5 text-xs">
            <ConfigChip enabled={data.email_enabled} icon={Mail} label="Email global" />
            <ConfigChip enabled={data.whatsapp_enabled} icon={MessageCircle} label="WhatsApp global" />
          </div>
        )}

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {loading && <div className="py-12 text-center text-sm text-zinc-500">Se încarcă…</div>}
          {err && !loading && <div className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">{err}</div>}
          {!loading && !err && data && flows.map((flow) => {
            const rows = data.steps.filter((s) => s.flow === flow);
            const color = FLOW_COLORS[flow] || "zinc";
            return (
              <div key={flow} className="mb-6" data-testid={`audit-flow-${flow}`}>
                <div className={`mb-2 inline-flex items-center gap-2 rounded-md border border-${color}-500/40 bg-${color}-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-${color}-200`}>
                  {FLOW_LABELS[flow]}
                </div>
                <div className="overflow-hidden rounded-lg border border-zinc-800">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-zinc-900/70 text-[10px] uppercase tracking-wider text-zinc-500">
                      <tr>
                        <th className="w-[30%] px-3 py-2 text-left font-medium">Pas</th>
                        <th className="w-[35%] px-3 py-2 text-left font-medium"><Mail className="mr-1 inline h-3 w-3" />Email</th>
                        <th className="w-[35%] px-3 py-2 text-left font-medium"><MessageCircle className="mr-1 inline h-3 w-3" />WhatsApp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.status} className="border-t border-zinc-900" data-testid={`audit-step-${flow}-${r.status}`}>
                          <td className="px-3 py-2.5 align-top">
                            <div className="text-xs font-medium text-zinc-100">{r.label}</div>
                            <div className="mt-0.5 font-mono text-[9px] text-zinc-600">{r.status}</div>
                          </td>
                          <td className="px-3 py-2.5 align-top">
                            <ChannelCell row={r.email} channel="email" />
                          </td>
                          <td className="px-3 py-2.5 align-top">
                            <ChannelCell row={r.whatsapp} channel="whatsapp" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ConfigChip({ enabled, icon: Icon, label }) {
  if (enabled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-300">
        <Icon className="h-3 w-3" /> {label}: activ
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-rose-300">
      <Icon className="h-3 w-3" /> {label}: DEZACTIVAT
    </span>
  );
}

function ChannelCell({ row }) {
  if (row.ok) {
    return (
      <div className="flex items-start gap-1.5">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
        <div className="min-w-0">
          <div className="truncate text-[11px] font-medium text-emerald-200">{row.template_name}</div>
          {row.subject && <div className="mt-0.5 truncate text-[10px] text-zinc-500">{row.subject}</div>}
          {row.meta_status && (
            <div className="mt-0.5 inline-flex rounded-sm border border-emerald-500/30 bg-emerald-500/10 px-1 text-[9px] font-bold uppercase text-emerald-300">
              {row.meta_status}
            </div>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-1.5">
      <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-400" />
      <div className="min-w-0">
        <div className="text-[11px] text-rose-200">Nu se trimite</div>
        <div className="mt-0.5 text-[10px] text-zinc-500">{row.reason}</div>
        {row.template_name && (
          <div className="mt-0.5 truncate font-mono text-[10px] text-zinc-600">{row.template_name}</div>
        )}
        {row.meta_status && row.meta_status !== "APPROVED" && (
          <div className="mt-0.5 inline-flex rounded-sm border border-amber-500/30 bg-amber-500/10 px-1 text-[9px] font-bold uppercase text-amber-300">
            {row.meta_status}
          </div>
        )}
      </div>
    </div>
  );
}
